/**
 * CoverageTab — structural view of the subtask manifest for a section.
 *
 * Phase 1 (this commit): structural only. Renders the subtask checklist
 * from the AuditRecord — writer sees which pieces of the instruction
 * the agent claimed to cover, in order. No per-subtask coverage verdict
 * yet — that will be powered by a future coverage-scoring pass
 * (``<module>_coverage.json`` sidecar artifact) and will light up the
 * existing SubtaskCard rows with chips without UI rework.
 *
 * Axes recap (see the IA design notes in the session log):
 *   - Coverage (this tab): "did the agent address every part of the
 *     instruction?" — subtask-first, mechanical comparison.
 *   - Review: "would a regulator accept this section?" — persona-first,
 *     rubric-anchored judgment.
 *   - Sources: "does every claim trace to a retrieved source?"
 * Three orthogonal failure modes, three surfaces.
 *
 * SRP: this component owns "render the coverage checklist for one
 * (session, module)". Data fetch lives in useReviewAgent. Row visuals
 * live in SubtaskCard. Nothing else.
 *
 * IA-agnostic: both the classic tabbed UI (inside SectionDetailPanel)
 * and any future chat-first or dashboard surface can host it with a
 * single ``<CoverageTab sessionId=... moduleId=... />`` call.
 */

import { Caption1, Spinner, Text, makeStyles, tokens } from "@fluentui/react-components";
import { InfoRegular } from "@fluentui/react-icons";
import type React from "react";
import { useReviewAgent } from "../../hooks/useReviewAgent";
import { SubtaskCard } from "./SubtaskCard";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  status: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM,
    color: tokens.colorNeutralForeground2,
  },
  infoBanner: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    color: tokens.colorNeutralForeground2,
  },
  errorBanner: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteRedBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorPaletteRedBorderActive}`,
  },
  errorTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorPaletteRedForeground1,
  },
  futureNote: {
    fontStyle: "italic",
    color: tokens.colorNeutralForeground3,
  },
});

export interface CoverageTabProps {
  /** Session identifier — ``<trial>__<session>`` per the sidecar's
   *  naming convention (see review_agent/watcher.py _infer_session_id). */
  sessionId: string;
  /** Module identifier (e.g. "PSURP-01") — the ``target`` in the
   *  sidecar's URL scheme. */
  moduleId: string;
}

/**
 * CoverageTab — render the subtask checklist for a single module.
 */
export const CoverageTab: React.FC<CoverageTabProps> = ({ sessionId, moduleId }) => {
  const styles = useStyles();
  const { audit, loading, error, notYetProduced } = useReviewAgent({
    session: sessionId,
    target: moduleId,
  });

  if (loading) {
    return (
      <div className={styles.status}>
        <Spinner size="extra-tiny" />
        <span>Loading coverage checklist…</span>
      </div>
    );
  }

  if (error) {
    // Sidecar unreachable (network / 5xx). Actionable message pointing
    // the operator at the likely fix without showing a raw stack trace.
    return (
      <div className={styles.errorBanner}>
        <Text className={styles.errorTitle}>Coverage unavailable</Text>
        <Caption1>
          review_agent /audit/{sessionId}/{moduleId} → {error.status}: {error.message}
        </Caption1>
        <Caption1>
          Check that the review_agent sidecar is running on <code>http://localhost:8001</code>.
        </Caption1>
      </div>
    );
  }

  if (notYetProduced || !audit) {
    // Expected steady state while the sidecar watcher hasn't picked up
    // the module log yet, or before the watcher has produced its first
    // audit for this module.
    return (
      <div className={styles.infoBanner}>
        <InfoRegular />
        <div>
          <Text block>Not yet audited</Text>
          <Caption1>
            The reviewer hasn't produced a coverage record for this module yet. The subtask checklist will appear here
            once the sidecar picks up the pipeline log.
          </Caption1>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <SubtaskCard moduleId={moduleId} subtasks={audit.subtasks} parseStatus={audit.parse_status} />
      {/* Forward-looking note — harmless, brief, and honest about the
          axis boundary. Removed once the coverage-scoring pass lands
          and per-row chips render real verdicts. */}
      <Caption1 className={styles.futureNote}>
        Structural checklist only. Per-subtask coverage verdicts will appear once the coverage-scoring pass is active.
      </Caption1>
    </div>
  );
};

export default CoverageTab;
